import React from "react";
import { Avatar, Checkbox, Dropdown, message, Tooltip } from "antd";
import {
  PaperClipOutlined,
  MessageOutlined,
  EditOutlined,
  DeleteOutlined,
  CopyOutlined,
  MoreOutlined,
  DragOutlined,
  BugOutlined,
  RocketOutlined,
  ToolOutlined,
  CheckSquareOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  StopOutlined,
  CheckCircleOutlined,
  LinkOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import { SimpleTagManager as TagManager } from "./SimpleTagManager";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import DOMPurify from "dompurify";
import {
  convertArrayRefsToFileService,
  stripImagesToAltText,
} from "utils/taskUtils";
import { getTaskAssigneeInfo } from "utils/taskAssignee";
import paths from "routes/paths";

const statusIcons = {
  OPEN: <ClockCircleOutlined style={{ color: "#6A1B9A" }} />,
  IN_PROGRESS: <SyncOutlined spin style={{ color: "#1565C0" }} />,
  BLOCKED: <StopOutlined style={{ color: "#B71C1C" }} />,
  COMPLETED: <CheckCircleOutlined style={{ color: "#2E7D32" }} />,
  CANCELLED: <StopOutlined style={{ color: "#B71C1C" }} />,
  RESOLVED: <CheckCircleOutlined style={{ color: "#2E7D32" }} />,
  BACKLOG: <BugOutlined style={{ color: "#2E7D32" }} />,
};
export const statusColors = {
  COMPLETED: {
    bg: "#E8F7E3",
    text: "#2E7D32",
    border: "#D5EBD0",
  },

  // OTW: {
  //   bg: "#FFF8E1",
  //   text: "#ED6C02",
  //   border: "#FFE0B2",
  // },

  IN_PROGRESS: {
    bg: "#E3F2FD",
    text: "#1565C0",
    border: "#BBDEFB",
  },

  CANCELLED: {
    bg: "#FDECEA",
    text: "#C62828",
    border: "#F5C6C3",
  },

  EXPIRED: {
    bg: "#F5F5F5",
    text: "#616161",
    border: "#E0E0E0",
  },

  OPEN: {
    bg: "#F3E8FF",
    text: "#6A1B9A",
    border: "#E1BEE7",
  },

  BLOCKED: {
    bg: "#FDECEA",
    text: "#B71C1C",
    border: "#F5C6C3",
  },
};

const priorityColors = {
  HIGH: {
    bg: "#FDECEA",
    text: "#C62828",
    border: "#F5C6C3",
  },

  MEDIUM: {
    bg: "#FFF8E1",
    text: "#ED6C02",
    border: "#FFE0B2",
  },

  LOW: {
    bg: "#E3F2FD",
    text: "#1565C0",
    border: "#BBDEFB",
  },
};

const getPriorityLabel = (priority) => {
  if (priority === 0) return "HIGH";
  if (priority === 1 || priority === 2) return "MEDIUM";
  if (priority > 3) return "LOW";
  return "Medium";
};

const getPriorityStyle = (priority) => {
  const label = getPriorityLabel(priority);

  return (
    priorityColors[label] || {
      bg: "#F3F4F6",
      text: "#374151",
      border: "#E5E7EB",
    }
  );
};

export const customStyles = {
  backgroundColor: "#e8f7e3",
  color: "#2e7d32",
  padding: "3px 8px",
  borderRadius: "3px",
  fontSize: "0.65rem",
  border: "1px solid #d5ebd0",
  cursor: "pointer",
  fontWeight: 500,
};

const typeConfig = {
  BUG: {
    icon: <BugOutlined />,
    color: "#C62828",
    bg: "#FDECEA",
  },
  FEATURE_REQUEST: {
    icon: <RocketOutlined />,
    color: "#6A1B9A",
    bg: "#F3E8FF",
  },
  IMPROVEMENT: {
    icon: <ToolOutlined />,
    color: "#1565C0",
    bg: "#E3F2FD",
  },
  TASK: {
    icon: <CheckSquareOutlined />,
    color: "#2E7D32",
    bg: "#E8F7E3",
  },
};

const TaskCard = ({
  task,
  onClick,
  onEditTask,
  onCloneTask,
  onTransferTask,
  onDelete,
  isDragging,
  onAssigneeClick,
  isMultiSelectMode,
  isSelected,
  onToggleSelect,
}) => {
  const assignee = getTaskAssigneeInfo(task || {});
  const assigneeName = assignee.label || "Unknown";
  const assigneeKey = task?.assigneeKey || assignee.key || assigneeName;

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: task.id,
      disabled: isMultiSelectMode,
    });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    // cursor: "grab",
  };

  const shareUrl =
    window.location.origin +
    paths.projectTaskDetails(task.projectId, task.iterationId, task.id);

  const handleCopyLink = () => {
    navigator.clipboard
      .writeText(shareUrl)
      .then(() => message.success("Link copied to clipboard"))
      .catch(() => message.error("Failed to copy link"));
  };

  const items = [
    {
      key: "copy",
      icon: <LinkOutlined />,
      label: "Copy Link",
      onClick: ({ domEvent }) => {
        domEvent.stopPropagation();
        handleCopyLink();
      },
    },
    {
      key: "clone",
      icon: <CopyOutlined />,
      label: "Clone Task",
      onClick: ({ domEvent }) => {
        domEvent.stopPropagation();
        onCloneTask?.(task);
      },
    },
    {
      key: "transfer",
      icon: <SwapOutlined />,
      label: "Transfer Task",
      onClick: ({ domEvent }) => {
        domEvent.stopPropagation();
        onTransferTask?.(task);
      },
    },
    {
      key: "edit",
      icon: <EditOutlined />,
      label: "Edit",
      onClick: ({ domEvent }) => {
        domEvent.stopPropagation();
        onEditTask(task);
      },
    },
    {
      key: "delete",
      icon: <DeleteOutlined />,
      label: "Delete",
      danger: true,
      onClick: ({ domEvent }) => {
        domEvent.stopPropagation();
        onDelete(task);
      },
    },
  ];

  const statusStyle = statusColors[task.status] || {
    bg: "#F3F4F6",
    text: "#374151",
    border: "#E5E7EB",
  };

  const priorityStyle = getPriorityStyle(task?.priority);

  const typeStyle = typeConfig[task.type] || {};
  const typeColor = typeConfig[task.type]?.color || "#e5e7eb";

  const isCompleted = task.status === "COMPLETED";

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        borderLeft: `4px solid ${typeColor}`,
      }}
      className={`task-card ${isSelected ? "selected" : ""}`}
      onClick={(e) => {
        if (isMultiSelectMode) {
          e.stopPropagation();
          onToggleSelect(task.id, !isSelected);
        } else {
          onClick?.();
        }
      }}
    >
      {isMultiSelectMode && !isCompleted && (
        <Checkbox
          checked={isSelected}
          onChange={(e) => {
            e.stopPropagation();
            onToggleSelect(task.id, e.target.checked);
          }}
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "absolute",
            top: 0,
            left: 3,
            zIndex: 10,
          }}
        />
      )}

      {/* drag handle */}
      <div
        className="drag-handle"
        {...attributes}
        {...listeners}
        onClick={(e) => e.stopPropagation()}
      >
        <DragOutlined />
      </div>

      <div className="task-tags">
        <div
          className="task-category"
          style={{
            ...customStyles,
            backgroundColor: statusStyle.bg,
            color: statusStyle.text,
            border: `1px solid ${statusStyle.border}`,
          }}
        >
          <Tooltip
            title={task.status.replace("_", " ")}
            styles={{
              root: { fontFamily: "Inter", fontSize: 10, fontWeight: 600 },
            }}
          >
            {statusIcons[task.status]}
          </Tooltip>
        </div>

        <span
          className="task-priority"
          style={{
            ...customStyles,
            backgroundColor: priorityStyle.bg,
            color: priorityStyle.text,
            border: `1px solid ${priorityStyle.border}`,
          }}
        >
          {getPriorityLabel(task?.priority)}
        </span>

        <span
          className="task-type"
          style={{
            ...customStyles,
            backgroundColor: typeStyle.bg,
            color: typeStyle.color,
            border: `1px solid ${typeStyle.color}33`,
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <Tooltip
            title={task.type.replace("_", " ")}
            styles={{
              root: { fontFamily: "Inter", fontSize: 10, fontWeight: 600 },
            }}
          >
            {typeStyle.icon}
          </Tooltip>
        </span>
      </div>

      {/* context menu */}
      <Dropdown menu={{ items }} trigger={["click"]} placement="bottomRight">
        <MoreOutlined
          onClick={(e) => e.stopPropagation()}
          style={{
            cursor: "pointer",
            color: "#9ca3af",
            fontSize: 16,
            position: "absolute",
            top: 20,
            right: 10,
          }}
        />
      </Dropdown>

      <h4 className="task-card-title">{task?.title}</h4>
      <div
        className="task-card-desc"
        style={{
          color: "#374151",
          fontSize: 13,
          margin: 0,
          padding: 0,
          maxHeight: 130,
          overflow: "hidden",
        }}
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(
            stripImagesToAltText(
              convertArrayRefsToFileService(task?.description || ""),
              "[image]",
            ) || "<p>No description provided</p>",
          ),
        }}
      />
      <style>
        {`
          .task-card-desc img {
            max-width: 100%;
            max-height: 110px;
            width: auto;
            height: auto;
            object-fit: contain;
            display: block;
          }
          .task-card-desc p {
            margin-bottom: 0.35rem;
          }
        `}
      </style>

      <div className="task-divider" />

      <div className="task-footer">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            cursor: "pointer",
          }}
          onClick={(e) => {
            e.stopPropagation();
            onAssigneeClick(assigneeKey);
          }}
        >
          <Avatar size="small">{assigneeName.charAt(0).toUpperCase()}</Avatar>

          <span
            style={{ fontSize: 12, color: "#374151", fontWeight: 400 }}
            className="assignee-name"
            title={assignee.assigneeEmail || "No assignee email available"}
          >
            {assigneeName}
          </span>
        </div>

        <div className="task-meta">
          <span>
            <PaperClipOutlined /> {task?.links?.length || 0}
          </span>
          <span>
            <MessageOutlined /> {task?.comments?.length || 0}
          </span>
        </div>
      </div>
    </div>
  );
};

export default TaskCard;
